import ReturnStatusBadge from '@c/dashboard/ReturnStatusBadge';
import TrackReturnModal from '@c/modals/TrackReturnModal';
import Button from '@ui/Button';
import Chip from '@ui/Chip';
import SafeImage from '@ui/SafeImage';
import { calculateEarnings } from '@util/firestore/orders';
import { useOrder } from '@util/hooks/useOrder';
import { useProduct } from '@util/hooks/useProduct';
import { useTrackingInfo } from '@util/hooks/useTrackingInfo';
import { formatTimestamp, formatCurrency } from '@util/index';
import { ReturnDocument, ReturnState } from 'models/returns';
import { useState } from 'react';
import { MarkReturnAsReceivedModal } from './MarkReturnAsReceivedModal';
import Link from 'next/link';
import ReturnActions from './ReturnActions';

export function ReturnCard({
  ret,
  variant,
  hideHeader,
  showReturnLink,
}: {
  ret: ReturnDocument;
  variant: 'buyer' | 'seller' | 'warehouse';
  hideHeader?: boolean;
  showReturnLink?: boolean;
}) {
  const { data: productQuery } = useProduct(ret.product_id);

  const { data: orderQuery } = useOrder(ret.order_id);
  const product = productQuery?.data;
  const order = orderQuery?.data;
  const item = Object.values(order?.sellers ?? {})
    .flat()
    .find((item) => item.id === ret.product_id);
  if (item) item.refund_amount = 0;

  const { data: trackingQuery } = useTrackingInfo(
    ret.tracking_number || undefined,
    ret.carrier || undefined
  );
  const tracking = trackingQuery?.data;
  const estimatedDelivery = tracking?.estimated_delivery_date;

  if (!product) return null;

  const image = item?.variations
    ? product.variations?.find((v) => {
        const size = (
          item.variations?.letter ?? item.variations?.number
        )?.toString();
        const vSize = v.size.toString();
        const color = item.variations?.color;
        const vColor = v.color;
        return (
          (size === vSize && (!color || color === vColor)) ||
          (!size && color === vColor)
        );
      })?.image ?? product?.images[0]?.thumb
    : product?.images[0]?.thumb;
  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
      {!hideHeader && (
        <div className="flex flex-col justify-between gap-2 rounded-t-2xl border-b border-gray-200 bg-[#F6F6F6]  p-8 sm:flex-row">
          <div className="flex flex-col gap-4">
            <Link
              href={`/dashboard/returns/${ret.id}`}
              className="whitespace-nowrap text-[2.2rem] font-semibold"
            >
              Return #{order?.order_num}
            </Link>
            <span className="whitespace-nowrap text-[1.6rem] text-gray-500">
              Requested at {formatTimestamp(ret.created)}
            </span>
          </div>
          {ret.final_amount ? (
            <span className="hidden whitespace-nowrap text-[2.2rem] font-semibold sm:block">
              Refunded: {formatCurrency(ret.final_amount)}
            </span>
          ) : (
            <span className="hidden whitespace-nowrap text-[2.2rem] font-semibold sm:block">
              Expected Refund: {formatCurrency(ret.eligible_refund_amount)}
            </span>
          )}
        </div>
      )}
      <div className="flex w-full flex-col items-end justify-between p-4 sm:flex-row">
        <div className="flex flex-col gap-4 p-4 pt-0">
          <ReturnStatusBadge
            state={ret.state}
            date={
              estimatedDelivery
                ? new Date(estimatedDelivery).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : undefined
            }
            variant={variant}
            returnDoc={ret}
            showReturnLink={showReturnLink}
          />
          <div className="flex flex-col gap-2">
            <span className="text-[1.8rem] font-semibold">Returned Item:</span>
            <Link
              href={`/product/${product?.slug}`}
              className="flex items-center gap-3 "
              target="_blank"
            >
              {image && (
                <div className="relative h-[10rem] w-[10rem] shrink-0">
                  <SafeImage
                    src={image}
                    alt={product?.title || ret.product_id}
                    fill
                    className="aspect-square object-contain"
                  />
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-[1.6rem]">
                  {product?.title || ret.product_id}
                </span>
                {ret.refund_reason && (
                  <ul className="flex list-inside list-disc flex-col gap-1">
                    <li className="text-[1.4rem] text-gray-500">
                      Return Reason: {ret.refund_reason}
                    </li>
                  </ul>
                )}
              </div>
            </Link>
          </div>
        </div>
        <ReturnActions ret={ret} variant={variant} item={item} order={order} />
      </div>
    </div>
  );
}
